import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { createMenuSet, ICreateMenuSetRequest } from 'src/api/createMenuSet';
import { IMenuSetDetail, IMenuSetResponseBody } from 'src/api/getMenuSet';
import { useUserTracking } from '../useUserTracking/useUserTracking';
import { QueryKeys } from '../QueryKeys';

/**
 * Creates a menu set, then refreshes the menu sets list and confirms with a snackbar. Errors are left
 * to the caller, as how they're shown depends on the form.
 */
export const useCreateMenuSet = (onMutationSuccess: (menuSet: IMenuSetDetail) => void) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();

  return useMutation<IMenuSetResponseBody, Error, ICreateMenuSetRequest>(createMenuSet, {
    onSuccess: async ({ data: [menuSet] }) => {
      trackEvent('menu_set_created', { menuSetName: menuSet.name, menuCount: menuSet.menus.length });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSets] });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: `You have successfully created ${menuSet.name}.` });
      onMutationSuccess(menuSet);
    },
    onError: (error, { name }) => {
      captureException(error, { tags: { action: 'menu_set_create', menuSetName: name } });
    },
  });
};
